<div class="plan-debug">
  <!-- Header (56px): identity icon + title + full plan_ref/revision/state, Plan JSON action + close -->
  <h2 mat-dialog-title class="plan-debug__header">
    <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">bug_report</mat-icon>
    <span class="mat-font-title-md">Plan debug</span>
    <span class="mat-font-label-md mat-text-on-surface-variant">
      {{ plan().plan_ref }} · rev. {{ plan().revision }} · {{ plan().state ?? "—" }}
    </span>
    <span class="d-flex align-items-center gap-2 ms-auto">
      <button matButton type="button" (click)="openPlanJson()">
        <mat-icon class="mat-icon-sm" aria-hidden="true">data_object</mat-icon>
        Plan JSON
      </button>
      <button matIconButton mat-dialog-close type="button" aria-label="Close debug dialog">
        <mat-icon class="mat-icon-md" aria-hidden="true">close</mat-icon>
      </button>
    </span>
  </h2>

  <div class="plan-debug__body">
    <!-- Left index: 36px pills, active = secondary-container, counter at the right when countable -->
    <nav class="plan-debug__index" aria-label="Debug sections">
      @for (section of sections; track section.id) {
        <button
          type="button"
          class="plan-debug__index-item mat-corner-full mat-font-label-lg mat-text-on-surface-variant"
          [class.plan-debug__index-item--active]="activeSection() === section.id"
          (click)="scrollToSection(section.id)"
        >
          <mat-icon class="mat-icon-sm" aria-hidden="true">{{ section.icon }}</mat-icon>
          <span>{{ section.label }}</span>
          @if (sectionCounts()[section.id]; as count) {
            <span class="plan-debug__index-count mat-font-label-sm">{{ count }}</span>
          }
        </button>
      }
    </nav>

    <!-- Right content column: 13 sections, continuous scroll, full text everywhere -->
    <div class="plan-debug__content">
      <!-- 1 · Meta -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="meta">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">info</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Meta</h3>
        </header>
        <div class="plan-debug__grid">
          @for (row of metaRows(); track row.label) {
            <div>
              <div class="mat-font-label-sm mat-text-on-surface-variant">{{ row.label }}</div>
              <div class="mat-font-body-md">{{ row.value }}</div>
            </div>
          }
        </div>
      </section>

      <!-- 2 · Phases: one row per phase record; decision/retries/confidence only where a contract exists -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="phases">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">account_tree</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Phases</h3>
          @if (sectionCounts().phases; as count) {
            <span class="notification-badge mat-font-label-sm ms-auto">{{ count }}</span>
          }
        </header>
        <div class="plan-debug__table">
          <div class="plan-debug__phase-row mat-font-label-sm mat-text-on-surface-variant">
            <span></span>
            <span>Phase</span>
            <span>Decision</span>
            <span>Retries</span>
            <span>Confidence</span>
          </div>
          @for (phase of phaseRows(); track phase.key) {
            <div class="plan-debug__phase-row">
              <span class="d-flex align-items-center">
                @if (phase.spinning) {
                  <mat-icon class="mat-icon-md spin-icon mat-text-primary" [matTooltip]="phase.tooltip" aria-hidden="true">
                    sync
                  </mat-icon>
                } @else {
                  <mat-icon class="mat-icon-md" [class]="phase.toneClass" [matTooltip]="phase.tooltip" aria-hidden="true">
                    {{ phase.statusIcon }}
                  </mat-icon>
                }
              </span>
              <span class="mat-font-body-md">{{ phase.label }}</span>
              <span>
                @if (phase.decisionBadge; as badge) {
                  <span class="notification-badge mat-font-label-sm" [class]="'notification-badge--' + badge.variant">
                    {{ badge.text }}
                  </span>
                } @else {
                  <span class="mat-font-body-sm mat-text-on-surface-variant">{{ phase.decisionText }}</span>
                }
              </span>
              <span class="mat-font-body-sm" matTooltip="contract verdict_block retry_count">{{ phase.retriesText }}</span>
              <span class="d-flex align-items-center">
                @if (phase.confidence !== null) {
                  <app-confidence-badge [value]="phase.confidence" />
                } @else {
                  <span class="mat-font-body-sm mat-text-on-surface-variant">—</span>
                }
              </span>
            </div>
          }
        </div>
      </section>

      <!-- 3 · Facts: raw ids as meta, value as full-width mono JSON (objects) or inline -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="facts">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">fact_check</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Facts</h3>
          @if (sectionCounts().facts; as count) {
            <span class="notification-badge mat-font-label-sm ms-auto">{{ count }}</span>
          }
        </header>
        @if (factViews().length > 0) {
          <div class="plan-debug__grid">
            @for (fact of factViews(); track fact.factId) {
              <div class="wide d-flex flex-column gap-1">
                <div class="mat-font-label-sm mat-text-on-surface-variant">{{ fact.factId }}</div>
                <div class="mat-font-body-sm mat-text-on-surface-variant">{{ fact.metaText }}</div>
                @if (fact.valueIsObject) {
                  <pre class="plan-debug__mono mb-0">{{ fact.valueJson }}</pre>
                } @else {
                  <div class="mat-font-body-md">{{ fact.valueInline }}</div>
                }
              </div>
            }
          </div>
        } @else {
          <div class="mat-font-body-md mat-text-on-surface-variant">—</div>
        }
      </section>

      <!-- 4 · Profile: goals, target, batch, sampling + per-task entities and workareas -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="profile">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">person_search</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Profile</h3>
        </header>
        <div class="plan-debug__grid">
          <div class="wide">
            <div class="mat-font-label-sm mat-text-on-surface-variant">goals</div>
            @if (profileView().goals.length > 0) {
              <div class="d-flex flex-wrap gap-1">
                @for (goal of profileView().goals; track $index) {
                  <span class="notification-badge mat-font-label-sm notification-badge--pending">{{ goal }}</span>
                }
              </div>
            } @else {
              <div class="mat-font-body-md mat-text-on-surface-variant">—</div>
            }
          </div>
          <div>
            <div class="mat-font-label-sm mat-text-on-surface-variant">target_entity</div>
            <div class="mat-font-body-md">{{ profileView().targetText }}</div>
          </div>
          <div>
            <div class="mat-font-label-sm mat-text-on-surface-variant">batch</div>
            <div class="mat-font-body-md">{{ profileView().batchText }}</div>
          </div>
          <div>
            <div class="mat-font-label-sm mat-text-on-surface-variant">sampling</div>
            <div class="mat-font-body-md">{{ profileView().samplingText }}</div>
          </div>
          <div class="wide">
            <div class="mat-font-label-sm mat-text-on-surface-variant">sampling reason</div>
            <div class="mat-font-body-md">{{ profileView().samplingReason }}</div>
          </div>
          @for (entity of profileView().taskEntities; track entity.taskId) {
            <div>
              <div class="mat-font-label-sm mat-text-on-surface-variant">{{ entity.taskId }}</div>
              <div class="mat-font-body-md">{{ entity.text }}</div>
            </div>
          }
          @for (workarea of profileView().workareas; track workarea.workareaId) {
            <div>
              <div class="mat-font-label-sm mat-text-on-surface-variant">{{ workarea.workareaId }}</div>
              <div class="mat-font-body-md">{{ workarea.usedByText }}</div>
            </div>
            <div class="wide">
              <div class="mat-font-label-sm mat-text-on-surface-variant">workarea reason</div>
              <div class="mat-font-body-md">{{ workarea.reason }}</div>
            </div>
          }
        </div>
      </section>

      <!-- 5 · Estimations: one grouped block per estimation_plan entry -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="estimations">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">calculate</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Estimations</h3>
        </header>
        @if (estimationViews().length > 0) {
          <div class="d-flex flex-column gap-2">
            @for (entry of estimationViews(); track entry.taskId) {
              <div class="plan-debug__grid">
                <div>
                  <div class="mat-font-label-sm mat-text-on-surface-variant">task_id</div>
                  <div class="mat-font-body-md">{{ entry.taskId }}</div>
                </div>
                <div>
                  <div class="mat-font-label-sm mat-text-on-surface-variant">estimates_task_id</div>
                  <div class="mat-font-body-md">{{ entry.estimatesTaskId }}</div>
                </div>
                <div>
                  <div class="mat-font-label-sm mat-text-on-surface-variant">estimated_resolver_ids</div>
                  <div class="mat-font-body-md">{{ entry.resolversText }}</div>
                </div>
                <div>
                  <div class="mat-font-label-sm mat-text-on-surface-variant">fact_type</div>
                  <div class="mat-font-body-md">{{ entry.factType }}</div>
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="mat-font-body-md mat-text-on-surface-variant">—</div>
        }
      </section>

      <!-- 6 · Review: validator status icon + validator + reason with the muted refs line -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="review">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">rate_review</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Review</h3>
          @if (sectionCounts().review; as count) {
            <span class="notification-badge mat-font-label-sm ms-auto">{{ count }}</span>
          }
        </header>
        @if (reviewRows().length > 0) {
          <div class="plan-debug__table">
            @for (result of reviewRows(); track result.resultId) {
              <div class="plan-debug__review-row">
                <mat-icon class="mat-icon-md" [class]="result.toneClass" [matTooltip]="result.word" aria-hidden="true">
                  {{ result.icon }}
                </mat-icon>
                <div class="mat-font-body-md fw-medium">{{ result.validatorId }}</div>
                <div class="d-flex flex-column">
                  <div class="mat-font-body-md">{{ result.reason }}</div>
                  @if (result.refsText) {
                    <div class="mat-font-label-sm mat-text-on-surface-variant">{{ result.refsText }}</div>
                  }
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="mat-font-body-md mat-text-on-surface-variant">—</div>
        }
      </section>

      <!-- 7 · Tasks: live state icon + id/attempts + last-attempt resolver/binding + diagnostics -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="tasks">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">task_alt</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Tasks</h3>
          @if (sectionCounts().tasks; as count) {
            <span class="notification-badge mat-font-label-sm ms-auto">{{ count }}</span>
          }
        </header>
        @if (taskRows().length > 0) {
          <div class="plan-debug__table">
            <div class="plan-debug__task-row mat-font-label-sm mat-text-on-surface-variant">
              <span></span>
              <span>task_id</span>
              <span>resolver</span>
              <span>binding</span>
              <span>retries · hitl_reask</span>
            </div>
            @for (task of taskRows(); track task.taskId) {
              <div class="plan-debug__task-row">
                <mat-icon class="mat-icon-sm" [class]="task.toneClass" [matTooltip]="task.label" aria-hidden="true">
                  {{ task.icon }}
                </mat-icon>
                <div class="d-flex flex-column">
                  <span class="mat-font-body-md fw-medium">{{ task.taskId }}</span>
                  <span class="mat-font-body-sm mat-text-on-surface-variant">attempts: {{ task.attemptsCount }}</span>
                </div>
                <div class="mat-font-body-sm" matTooltip="attempts[-1].resolver_id">{{ task.resolverId }}</div>
                <div class="mat-font-body-sm" matTooltip="attempts[-1].input_bindings — role · confidence">
                  {{ task.bindingsText }}
                </div>
                <div class="mat-font-body-sm" matTooltip="diagnostics.action_retry_count · diagnostics.hitl_reask_count">
                  {{ task.retriesText }}
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="mat-font-body-md mat-text-on-surface-variant">—</div>
        }
      </section>

      <!-- 8 · Run config: batch consumers + sample size of the run pass -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="run_config">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">tune</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Run config</h3>
        </header>
        <div class="plan-debug__grid">
          @for (row of runConfigRows(); track row.label) {
            <div>
              <div class="mat-font-label-sm mat-text-on-surface-variant">{{ row.label }}</div>
              <div class="mat-font-body-md">{{ row.value }}</div>
            </div>
          }
        </div>
      </section>

      <!-- 9 · Pending decisions: humanized type + Mandatory/Optional + reason + resolved badge -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="pending_decisions">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">pending_actions</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Pending decisions</h3>
          @if (sectionCounts().pending_decisions; as count) {
            <span class="notification-badge mat-font-label-sm ms-auto">{{ count }}</span>
          }
        </header>
        @if (decisionViews().length > 0) {
          <div class="d-flex flex-column gap-2">
            @for (decision of decisionViews(); track $index) {
              <div class="d-flex flex-column">
                <div class="d-flex align-items-center gap-2">
                  <span class="mat-font-body-md fw-medium">{{ decision.typeText }}</span>
                  <span class="mat-font-label-sm mat-text-on-surface-variant">{{ decision.mandatoryText }}</span>
                  <span
                    class="notification-badge mat-font-label-sm ms-auto"
                    [class]="'notification-badge--' + decision.resolvedVariant"
                  >{{ decision.resolvedText }}</span>
                </div>
                <div class="mat-font-body-sm mat-text-on-surface-variant">{{ decision.reason }}</div>
              </div>
            }
          </div>
        } @else {
          <div class="mat-font-body-md mat-text-on-surface-variant">None</div>
        }
      </section>

      <!-- 10 · Transition: the dump carries state/reason/revision only -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="transition">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">swap_horiz</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Transition</h3>
        </header>
        <div class="plan-debug__grid">
          @for (row of transitionRows(); track row.label) {
            <div>
              <div class="mat-font-label-sm mat-text-on-surface-variant">{{ row.label }}</div>
              <div class="mat-font-body-md">{{ row.value }}</div>
            </div>
          }
        </div>
      </section>

      <!-- 11 · Diagnosis: top-level failure + run_engine diagnostics rollup -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="diagnosis">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">troubleshoot</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Diagnosis</h3>
        </header>
        @if (diagnosisView().hasAnything) {
          <div class="plan-debug__grid">
            @if (diagnosisView().failure; as failure) {
              <div class="wide">
                <div class="mat-font-label-sm mat-text-on-surface-variant">user_message</div>
                <div class="mat-font-body-md">{{ failure.userMessage }}</div>
              </div>
              <div class="wide">
                <div class="mat-font-label-sm mat-text-on-surface-variant">issue_codes</div>
                <div class="mat-font-body-md">{{ failure.issueCodesText }}</div>
              </div>
              <div class="wide">
                <div class="mat-font-label-sm mat-text-on-surface-variant">detail</div>
                <pre class="plan-debug__mono mb-0">{{ failure.detail }}</pre>
              </div>
            }
            <div class="wide">
              <div class="mat-font-label-sm mat-text-on-surface-variant">last_issue_code</div>
              <div class="mat-font-body-md">{{ diagnosisView().lastIssueCodeText }}</div>
              @for (row of diagnosisView().lastIssueCodeRows; track row.taskId) {
                <div class="mat-font-body-sm mat-text-on-surface-variant">{{ row.taskId }} · {{ row.code }}</div>
              }
            </div>
            <div class="wide" matTooltip="diagnostics.turn_retry_charge — sum when uniform, per-task rows when mixed">
              <div class="mat-font-label-sm mat-text-on-surface-variant">turn_retry_charge</div>
              <div class="mat-font-body-md">{{ diagnosisView().retryChargeText }}</div>
              @for (row of diagnosisView().retryChargeRows; track row.taskId) {
                <div class="mat-font-body-sm mat-text-on-surface-variant">{{ row.taskId }} · {{ row.charge }}</div>
              }
            </div>
            <div class="wide">
              <div class="mat-font-label-sm mat-text-on-surface-variant">pending_reason.kind</div>
              @if (diagnosisView().pendingRows.length > 0) {
                @for (row of diagnosisView().pendingRows; track row.taskId) {
                  <div class="mat-font-body-sm mat-text-on-surface-variant">{{ row.taskId }} · {{ row.kind }}</div>
                }
              } @else {
                <div class="mat-font-body-md">—</div>
              }
            </div>
          </div>
        } @else {
          <div class="mat-font-body-md mat-text-on-surface-variant">—</div>
        }
      </section>

      <!-- 12 · Evidence events: raw evidence_progress ring from the optional store (newest first).
           Column order (v1.1, one decision): the round-1 identity columns time/stage/status/tool_id
           stay first, the v1.1 semantic fields label/kind/layer sit between them and the round-1
           metric columns size/duration/detail/row — identity, semantics, metrics keeps both
           rounds' reading flow intact. label renders truncated with the FULL text as tooltip. -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="evidence_events">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">timeline</mat-icon>
          <h3 class="mat-font-title-sm mb-0">Evidence events</h3>
          @if (sectionCounts().evidence_events; as count) {
            <span class="notification-badge mat-font-label-sm ms-auto">{{ count }}</span>
          }
        </header>
        @if (evidenceRows().length > 0) {
          <div class="plan-debug__table">
            <div class="plan-debug__evidence-row mat-font-label-sm mat-text-on-surface-variant">
              <span matTooltip="at — epoch ms rendered as the local wall clock">time</span>
              <span>stage</span>
              <span>status</span>
              <span>tool_id</span>
              <span matTooltip="event.label — task-intent text the eventual evidence carries">label</span>
              <span matTooltip="event.kind — produced data kind (real on staging, summary on execute/completed)">kind</span>
              <span matTooltip="event.layer — resolved Vexcel layer (api_call events only)">layer</span>
              <span matTooltip="event.size_bytes (raw bytes)">size</span>
              <span matTooltip="event.duration_ms (raw milliseconds)">duration</span>
              <span>detail</span>
              <span matTooltip="event.row_index">row</span>
            </div>
            @for (row of evidenceRows(); track $index) {
              <div class="plan-debug__evidence-row">
                <span class="mat-font-body-sm">{{ row.time }}</span>
                <span class="mat-font-body-sm">{{ row.stage }}</span>
                <span class="mat-font-body-sm">{{ row.status }}</span>
                <span class="mat-font-body-sm">{{ row.toolId }}</span>
                <span class="plan-debug__evidence-label mat-font-body-sm" [matTooltip]="row.labelTooltip">{{ row.label }}</span>
                <span class="mat-font-body-sm">{{ row.kind }}</span>
                <span class="mat-font-body-sm">{{ row.layer }}</span>
                <span class="mat-font-body-sm">{{ row.sizeBytes }}</span>
                <span class="mat-font-body-sm">{{ row.durationMs }}</span>
                <span class="mat-font-body-sm">{{ row.detail }}</span>
                <span class="mat-font-body-sm">{{ row.rowIndex }}</span>
              </div>
            }
          </div>
        } @else {
          <div class="mat-font-body-md mat-text-on-surface-variant">—</div>
        }
      </section>

      <!-- 13 · History: archived closed revisions, note line per PlanRevision fields -->
      <section class="plan-debug__section mat-border-subtle mat-corner-lg" data-section="history">
        <header class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-md icon-primary" aria-hidden="true">history</mat-icon>
          <h3 class="mat-font-title-sm mb-0">History</h3>
          @if (sectionCounts().history; as count) {
            <span class="notification-badge mat-font-label-sm ms-auto">{{ count }}</span>
          }
        </header>
        @if (historyRows().length > 0) {
          <div class="plan-debug__table">
            @for (entry of historyRows(); track entry.revision) {
              <div class="plan-debug__history-row">
                <span class="notification-badge mat-font-label-sm notification-badge--pending">rev. {{ entry.revision }}</span>
                <span>
                  @if (entry.stateText !== null) {
                    <span class="notification-badge mat-font-label-sm" [class]="'notification-badge--' + entry.stateVariant">
                      {{ entry.stateText }}
                    </span>
                  } @else {
                    <span class="mat-font-body-sm mat-text-on-surface-variant">—</span>
                  }
                </span>
                <div class="d-flex flex-column">
                  <div class="mat-font-body-sm">{{ entry.objective }}</div>
                  @if (entry.requestedChange; as requestedChange) {
                    <div class="mat-font-label-sm mat-text-on-surface-variant">requested_change: {{ requestedChange }}</div>
                  }
                  <div class="mat-font-label-sm mat-text-on-surface-variant">{{ entry.noteMeta }}</div>
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="mat-font-body-md mat-text-on-surface-variant">—</div>
        }
      </section>
    </div>
  </div>
</div>